// deviceApi.js
import config from "../../config.js";
import axios from "axios";

export const handleApiError = (error, context = "") => {
  if (error.response) {
    console.warn(`${context} → API responded with status ${error.response.status}`, error.response.data);
  } else if (error.request) {
    console.error(`${context} → No response received from server`, error.request);
  } else {
    console.error(`${context} → Request setup error`, error.message);
  }
};

export const createTrackingDevice = async (deviceData) => {
  try {
    const resp = await axios.post(`${config.baseURL}/device`, deviceData, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, "createTrackingDevice");
    throw error;
  }
};

export const getAllTrackingDevices = async () => {
  try {
    const resp = await axios.get(`${config.baseURL}/device`, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, "getAllTrackingDevices");
    return [];
  }
};

export const getTrackingDeviceById = async (id) => {
  try {
    const resp = await axios.get(`${config.baseURL}/device/${id}`, { withCredentials: true });
    const device = resp.data.data;
    console.log('API Response:', resp.data);
    console.log('Device data:', device);

    const transformedData = {
      id: device.id,
      deviceId: device.deviceId,
      imeiNumber: device.imeiNumber,
      model: device.model,
      vendorBrand: device.vendorBrand,
      workingCondition: device.workingCondition,
      status: device.status,
      provider: device.provider,
      installationDate: device.installationDate ? device.installationDate.split('T')[0] : '',
      executiveFullName: device.executiveFullName,
      executivePhoneNumber: device.executivePhoneNumber,
      executiveEmail: device.executiveEmail,
      remarks: device.remarks,

      SimCard: device.simCard ? {
        id: device.simCard.id,
        simNumber: device.simCard.simNumber,
        imeiNumber: device.simCard.imeiNumber,
        currentPlan: device.simCard.currentPlan || '',
        planExpiry: device.simCard.planExpiry ? device.simCard.planExpiry.split('T')[0] : '',
        provider: device.simCard.provider || '',
      } : null,

      // Fix Vehicle mapping - handle single vehicle vs array
      Vehicle: device.vehicle ? {
        id: device.vehicle.id,
        name: device.vehicle.name,
        registrationNumber: device.vehicle.registrationNumber || device.vehicle.VehicleNumber,
        model: device.vehicle.model || device.vehicle.Model,
        organization: device.vehicle.organization ? {
          id: device.vehicle.organization.id,
          name: device.vehicle.organization.name,
          city: device.vehicle.organization.city,
        } : null
      } : null,

      deviceImageUrl: null,
      Document: []
    };


    if (device.documents && device.documents.length > 0) {
      // Find device image
      const deviceImage = device.documents.find(doc => doc.name === 'DeviceImage');
      if (deviceImage) {
        transformedData.deviceImageUrl = `${config.baseURL}/upload/image?fileName=${encodeURIComponent(deviceImage.location)}`;
      }

      // Find installation photos
      const installationPhotos = device.documents.filter(doc => doc.name === 'InstallationPhoto');
      if (installationPhotos.length > 0) {
        installationPhotos.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        transformedData.Document = installationPhotos.map((photo, index) => ({
          id: photo.id,
          url: `${config.baseURL}/upload/image?fileName=${encodeURIComponent(photo.location)}`,
          location: photo.location,
          name: photo.name,
          serverId: photo.id,
          createdAt: photo.createdAt,
          updatedAt: photo.updatedAt
        }));
      }
    }

    return { data: transformedData };
  } catch (error) {
    handleApiError(error, `getTrackingDeviceById(${id})`);
    throw error;
  }
};


export const updateTrackingDevice = async (deviceData) => {
  try {
    const { id, ...updateData } = deviceData;
    if (!id) {
      throw new Error('Device ID is required for update');
    }
    const resp = await axios.put(`${config.baseURL}/device/${id}`, updateData, { withCredentials: true });
    return resp.data;
  } catch (error) {
    handleApiError(error, "updateTrackingDevice");
    throw error;
  }
};

export const deleteTrackingDevice = async (id) => {
  try {
    const resp = await axios.delete(`${config.baseURL}/device/${id}`, {
      withCredentials: true
    });
    return resp.data;
  } catch (error) {
    handleApiError(error, `deleteTrackingDevice(${id})`);
    throw error;
  }
};
export const assignDeviceToVehicle = async (trackingDeviceId, vehicleId) => {
  try {
    const resp = await axios.post(
      `${config.baseURL}/tracking-devices/assign`,
      { trackingDeviceId, vehicleId },
      { withCredentials: true }
    );
    return resp.data;
  } catch (error) {
    handleApiError(error, `assignDeviceToVehicle(${trackingDeviceId}, ${vehicleId})`);
    throw error;
  }
};

export const getTrackingDevicesByOrgIdIndependent = async (organizationId) => {
  try {
    const resp = await axios.get(
      `${config.baseURL}/tracking-devices/organization/independent/${organizationId}`
    );
    return resp.data;
  } catch (error) {
    handleApiError(error, "getTrackingDevicesByOrgId");
    return [];
  }
};

export const formatDeviceData = (formData, isUpdate = false, existingSimCardId = null) => {
  const deviceData = {
    deviceId: formData.deviceId,
    imeiNumber: formData.imeiNumber || null,
    model: formData.model || null,
    vendorBrand: formData.vendorBrand || null,
    workingCondition: formData.workingCondition || null,
    status: formData.status,
    installationDate: formData.installationDate || null,
    organizationName: formData.organizationName || null,
    city: formData.city || null,
    site: formData.site || null,
    vehicleId: formData.vehicle?.id || null,
    vehicleName: formData.vehicle?.name || null,
    vehicleRegistrationNumber: formData.vehicleRegistrationNumber || null,
    vehicleModel: formData.vehicleModel || null,
    executiveFullName: formData.executiveFullName || null,
    executivePhoneNumber: formData.executivePhoneNumber || null,
    executiveEmail: formData.executiveEmail || null,
    remarks: formData.remarks || null,
    simCard: {
      // Fix property access - use correct path
      imeiNumber: formData.simCard?.simImei || null,
      provider: formData.simCard?.provider || null,
      planExpiry: formData.simCard?.nextRechargeDate
        ? new Date(formData.simCard.nextRechargeDate).toISOString()
        : null,
      simNumber: formData.simCard?.mobileNumber || null,
    }
  };

  if (isUpdate) {
    deviceData.id = formData.id;
    if (existingSimCardId) {
      deviceData.simCard.id = existingSimCardId;
    }
  }

  return deviceData;
};


export const validateDevice = (deviceData) => {
  const errors = [];

  // Only validate truly required fields
  if (!deviceData.deviceId?.trim()) {
    errors.push('Device ID is required');
  }

  if (!deviceData.status?.trim()) {
    errors.push('Status is required');
  }

  // Device IMEI validation
  if (deviceData.imeiNumber?.trim() && !isValidIMEI(deviceData.imeiNumber.trim())) {
    errors.push('Device IMEI number must be numeric');
  }

  // SIM IMEI validation
  if (deviceData.simCard?.imeiNumber?.trim() && !isValidIMEI(deviceData.simCard.imeiNumber.trim())) {
    errors.push('SIM IMEI must be numeric');
  }

  // Phone number validation
  if (deviceData.executivePhoneNumber?.trim() && !isValidPhoneNumber(deviceData.executivePhoneNumber.trim())) {
    errors.push('Executive phone number format is invalid');
  }

  // Mobile number validation (SIM card)
  if (deviceData.simCard?.simNumber?.trim() && !isValidPhoneNumber(deviceData.simCard.simNumber.trim())) {
    errors.push('SIM mobile number format is invalid');
  }

  // Email validation
  if (deviceData.executiveEmail?.trim() && !isValidEmail(deviceData.executiveEmail.trim())) {
    errors.push('Executive email format is invalid');
  }


  return {
    isValid: errors.length === 0,
    errors
  };
};

export { isValidIMEI, isValidPhoneNumber, isValidEmail };

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'INSTALLED':
      return 'green';
    case 'PENDING':
      return 'orange';
    case 'REMOVED':
      return 'red';
    default:
      return 'gray';
  }
};

export const getConditionColor = (condition) => {
  switch (condition?.toUpperCase()) {
    case 'WORKING':
      return 'green';
    case 'NOT_WORKING':
      return 'red';
    case 'NEEDS_REPAIR':
      return 'orange';
    default:
      return 'gray';
  }
};

export const WORKING_CONDITIONS = [
  { value: 'WORKING', label: 'Working' },
  { value: 'NOT_WORKING', label: 'Not Working' },
  { value: 'NEEDS_REPAIR', label: 'Needs Repair' }
];

export const INSTALL_STATUS = [
  { value: 'INSTALLED', label: 'Installed' },
  { value: 'PENDING', label: 'Not Installed' },
  { value: 'REMOVED', label: 'Removed' }
];

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidIMEI = (imei) => {
  const imeiRegex = /^[a-zA-Z0-9]+$/;  // Allows letters + numbers, any length
  return imeiRegex.test(imei);
};


const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^[0-9a-zA-Z+\s\-()]{7,30}$/;
  return phoneRegex.test(phone);
};

